// Numbers the Indian way: ₹1,23,456, 2.4 L, 1.1 Cr, 12.5K.

const SYMBOLS = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED ' };

export const isNum = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

export function money(value, currency = 'INR', { decimals } = {}) {
  if (!isNum(value)) return '—';
  const number = Number(value);
  const places = decimals ?? (Math.abs(number) < 100 && number % 1 !== 0 ? 2 : 0);
  const text = number.toLocaleString('en-IN', { minimumFractionDigits: places, maximumFractionDigits: places });
  return `${SYMBOLS[currency] ?? `${currency} `}${text}`;
}

export function compact(value) {
  if (!isNum(value)) return '—';
  const number = Number(value);
  const abs = Math.abs(number);
  if (abs >= 1e7) return `${(number / 1e7).toFixed(abs >= 1e8 ? 0 : 1).replace(/\.0$/, '')} Cr`;
  if (abs >= 1e5) return `${(number / 1e5).toFixed(abs >= 1e6 ? 0 : 1).replace(/\.0$/, '')} L`;
  if (abs >= 1e3) return `${(number / 1e3).toFixed(abs >= 1e4 ? 0 : 1).replace(/\.0$/, '')}K`;
  return number.toLocaleString('en-IN', { maximumFractionDigits: 1 });
}

export const count = (value) => (isNum(value) ? Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—');

export const moneyCompact = (value, currency = 'INR') => (isNum(value) ? `${SYMBOLS[currency] ?? `${currency} `}${compact(value)}` : '—');

// Trim trailing zeros after the decimal point only ("20" stays "20", "1.50" → "1.5").
const trimZeros = (text) => (text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text);

export const percent = (value, digits = 2) => (isNum(value) ? `${trimZeros(Number(value).toFixed(digits))}%` : '—');

export const ratio = (value) => (isNum(value) ? `${trimZeros(Number(value).toFixed(2))}×` : '—');

export function plural(n, one, many = `${one}s`) {
  return `${count(n)} ${Number(n) === 1 ? one : many}`;
}

export function dayLabel(value, now = new Date()) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const start = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((start(date) - start(now)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === -1) return 'Yesterday';
  if (diff === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}

export function timeLabel(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s?(am|pm)$/i, (m) => ` ${m.trim().toUpperCase()}`);
}

export function when(value, now = new Date()) {
  const day = dayLabel(value, now);
  if (!day) return '—';
  return ['Today', 'Yesterday'].includes(day) ? `${day}, ${timeLabel(value)}` : day;
}

export function ago(value, now = new Date()) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const seconds = Math.round((now - date) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  return when(value, now);
}

export const shortDate = (iso) => {
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/** "+12%" / "−8%" with whether that change is good for this metric. */
export function deltaInfo(delta, { lowerIsBetter = false } = {}) {
  if (!isNum(delta)) return null;
  const value = Number(delta);
  const good = value === 0 ? null : (lowerIsBetter ? value < 0 : value > 0);
  return { text: `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value).toFixed(Math.abs(value) >= 10 ? 0 : 1)}%`, good, up: value > 0 };
}
